// Persistent comment limit cache that survives component recreations

interface CommentLimitCacheEntry {
  commentLimit: number;
  imageHeight: number | undefined;
  commentCount: number;
  timestamp: number;
}

// Global cache for comment limits and image heights
const commentLimitCache = new Map<string, CommentLimitCacheEntry>();

// Track maximum observed comment height for better fallback estimation
let maxObservedCommentHeight = 0;


// Generate cache key for a post
const generateCacheKey = (postId: string, windowWidth: number, windowHeight: number): string => {
  return `${postId}-${windowWidth}x${windowHeight}`;
};

// Get cached comment limit result
export const getCachedCommentLimit = (
  postId: string, 
  windowWidth: number, 
  windowHeight: number
): CommentLimitCacheEntry | undefined => {
  const cacheKey = generateCacheKey(postId, windowWidth, windowHeight);
  return commentLimitCache.get(cacheKey);
};

// Set cached comment limit result
export const setCachedCommentLimit = (
  postId: string,
  commentCount: number,
  windowWidth: number,
  windowHeight: number,
  result: { commentLimit: number; imageHeight: number | undefined }
): void => {
  const cacheKey = generateCacheKey(postId, windowWidth, windowHeight);
  commentLimitCache.set(cacheKey, {
    ...result,
    commentCount,
    timestamp: Date.now(),
  });
};

// Get the cache entry for a post (same as getCachedCommentLimit now)
export const getLatestCacheForPost = (
  postId: string,
  windowWidth: number,
  windowHeight: number
): CommentLimitCacheEntry | undefined => {
  return getCachedCommentLimit(postId, windowWidth, windowHeight);
};

// Clean up old cache entries (optional - for memory management)
export const cleanupOldCacheEntries = (maxAgeMs: number = 30 * 60 * 1000): void => { // 30 minutes default
  const now = Date.now();
  const keysToDelete: string[] = [];
  
  commentLimitCache.forEach((entry, key) => {
    if (now - entry.timestamp > maxAgeMs) {
      keysToDelete.push(key);
    }
  });
  
  keysToDelete.forEach(key => commentLimitCache.delete(key));
};

// Get and update maximum observed comment height
export const getMaxObservedCommentHeight = (): number => {
  return maxObservedCommentHeight;
};

export const updateMaxObservedCommentHeight = (height: number): void => {
  if (height > maxObservedCommentHeight) {
    maxObservedCommentHeight = height;
  }
};

// Check if cache should be prerendered (no cached limit exists)
export const shouldPrerender = (postId: string, windowWidth: number, windowHeight: number): boolean => {
  const cacheKey = generateCacheKey(postId, windowWidth, windowHeight);
  return !commentLimitCache.has(cacheKey);
};


// Get cache stats for debugging
export const getCacheStats = () => {
  return {
    size: commentLimitCache.size,
    maxObservedCommentHeight,
    entries: Array.from(commentLimitCache.entries()),
  };
};